import { ThemeOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface TypeText {
    primaryLighter: string;
    primaryLight: string;
    primaryDefault: string;
    primaryDark: string;
    primaryDarkest: string;

    secondaryLighter: string;
    secondaryLight: string;
    secondaryDefault: string;
    secondaryDark: string;
    secondaryDarkest: string;

    successLighter: string;
    successLight: string;
    successDefault: string;
    successDark: string;
    successDarkest: string;

    warningLighter: string;
    warningLight: string;
    warningDefault: string;
    warningDark: string;
    warningDarkest: string;

    errorLighter: string;
    errorLight: string;
    errorDefault: string;
    errorDark: string;
    errorDarkest: string;

    infoLighter: string;
    infoLight: string;
    infoDefault: string;
    infoDark: string;
    infoDarkest: string;

    base50: string;
    base100: string;
    base200: string;
    base300: string;
    base400: string;
    base500: string;
    base600: string;
    base700: string;
    base800: string;
    base850: string;
    base900: string;
    base950: string;

    fontLighter: string;
    fontLight: string;
    fontDefault: string;
    fontDark: string;
    fontDarkest: string;

    white: string;
    fire: string;
    bodyBg: string;
  }
  interface Color {
    tertiary: string;
  }
  interface PaletteColor {
    text?: string;
  }
  interface SimplePaletteColorOptions {
    text?: string;
  }
  interface Palette {
    tertiary: PaletteColor;
    neutral: PaletteColor;
  }
  interface PaletteOptions {
    tertiary: SimplePaletteColorOptions;
    neutral: SimplePaletteColorOptions;
  }
}

interface Meta {
  footerText?: string;
  dietitianAppointment?: boolean;
  pageTitle?: string;
  androidApp?: string;
  iosApp?: string;
}

interface IGlobalTheme {
  [key: string]: ThemeOptions & Meta;
}

export const GlobalTheme: IGlobalTheme = {
  default: {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1440,
        xl: 1536,
      },
    },
    spacing: 4,
    // status: {
    //   danger: "#e53e3e",
    // },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
    },
    palette: {
      primary: {
        main: "#29BC62",
        dark: "#2a9553",
        light: "#3dd277",
        text: "#fff",
        contrastText: "#fff",
      },
      secondary: {
        main: "#F69300",
        dark: "#DB8400",
        light: "#FFC366",
        text: "#fff",
        contrastText: "#fff",
      },
      tertiary: {
        main: "#136FD7",
        dark: "#0b6ddb",
        light: "#1b77de",
        text: "#fff",
        contrastText: "#C2C4C7",
      },
      neutral: {
        text: "#8A8E99",
        dark: "#272C37",
        main: "#64748B",
        contrastText: "#fff",
      },
      error: {
        main: "#F44231",
        dark: "#DF1E0C",
        light: "#F6675A",
        text: "#fff",
        contrastText: "#fff",
      },
      success: {
        main: "#28A745",
        dark: "#1F8437",
        light: "#39D05D",
        text: "#fff",
        contrastText: "#fff",
      },
      warning: {
        main: "#FF9F05",
        dark: "#D28100",
        light: "#FFB338",
        text: "#fff",
        contrastText: "#fff",
      },
      info: {
        main: "#0698C9",
        dark: "#04789F",
        light: "#24C4F9",
        text: "#fff",
        contrastText: "#fff",
      },
      text: {
        primary: "#2C2E30",
        primaryLighter: "#EFFBF3",
        primaryLight: "#36D472",
        primaryDefault: "#29BC62",
        primaryDark: "#22944C",
        primaryDarkest: "#14582D",

        secondaryLighter: "#FFF9F0",
        secondaryLight: "#FFC366",
        secondaryDefault: "#F69300",
        secondaryDark: "#DB8400",
        secondaryDarkest: "#5C3700",

        successLighter: "#EFFBF1",
        successLight: "#39D05D",
        successDefault: "#28A745",
        successDark: "#1F8437",
        successDarkest: "#10421B",

        warningLighter: "#FEF5E6",
        warningLight: "#FFB338",
        warningDefault: "#FF9F05",
        warningDark: "#D28100",
        warningDarkest: "#663F00",

        errorLighter: "#FEEDEC",
        errorLight: "#F6675A",
        errorDefault: "#F44231",
        errorDark: "#DF1E0C",
        errorDarkest: "#6F0F06",

        infoLighter: "#EEF7FA",
        infoLight: "#24C4F9",
        infoDefault: "#0698C9",
        infoDark: "#04789F",
        infoDarkest: "#023C50",

        base50: "#F3F4F6",
        base100: "#E5E8EB",
        base200: "#D7DAE0",
        base300: "#CACDD4",
        base400: "#C1C5CD",
        base500: "#B0B5BF",
        base600: "#9DA3AF",
        base700: "#848B9A",
        base800: "#676F7E",
        base850: "#3E4451",
        base900: "#323743",
        base950: "#292D38",

        fontLighter: "#F1F2F3",
        fontLight: "#A1A6AB",
        fontDefault: "#54595F",
        fontDark: "#2C2E30",
        fontDarkest: "#18191B",

        white: "#fff",
        fire: "#f9720f",
        bodyBg: "#fff",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@fontFace": ["Inter"],
          },
          body: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textTransform: "capitalize",
            borderRadius: "12px",
            padding: theme.spacing(2, 3),
            ...(ownerState.variant === "contained" && ownerState.color === "primary"
              ? {
                  "&": { color: theme.palette.primary.text, backgroundColor: theme.palette.primary.main },
                  "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                }
              : ownerState.variant === "contained" && ownerState.color === "secondary"
                ? {
                    "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                    "&.MuiLoadingButton-root.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }
                : ownerState.variant === "contained" && ownerState.color === "secondary"
                  ? {
                      "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                      "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      "&:hover": { color: theme.palette.text.primary },
                    }
                  : ownerState.variant === "contained" && ownerState.color === "warning"
                    ? {
                        "&": {
                          color: theme.palette.text.secondaryDark,
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      }
                    : ownerState.variant === "contained" && ownerState.color === "error"
                      ? {
                          "&": {
                            color: theme.palette.text.errorDark,
                            backgroundColor: theme.palette.text.errorLighter,
                          },
                          "&:hover": { backgroundColor: theme.palette.text.errorLighter },
                        }
                      : ownerState.variant === "outlined" && ownerState.color === "primary"
                        ? {
                            "&": { color: theme.palette.primary.main, borderColor: theme.palette.primary.main },
                            "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.text,
                            },
                          }
                        : ownerState.variant === "outlined" && ownerState.color === "secondary"
                          ? {
                              "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                              "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.text,
                              },
                            }
                          : ownerState.variant === "contained" && ownerState.color === "secondary"
                            ? {
                                "&": { color: theme.palette.tertiary.main, borderColor: theme.palette.tertiary.main },
                                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              }
                            : {
                                backgroundColor: theme.palette.text.base100,
                                color: theme.palette.text.base900,
                                "&:hover": { backgroundColor: theme.palette.text.base100 },
                              }),
            ...(ownerState.size === "large"
              ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
              : ownerState.size === "medium"
                ? {
                    fontSize: "16px",
                    lineHeight: `${ownerState.variant === "outlined" ? "24px" : "26px"}`,
                    fontWeight: 500,
                    padding: theme.spacing(3, 4),
                  }
                : ownerState.size === "small"
                  ? { fontSize: "14px", lineHeight: "20px", fontWeight: 400, padding: theme.spacing(2, 3) }
                  : ownerState.size === "xsmall"
                    ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
                    : {}),
          }),
        },
      },
      // MuiLoadingButton: {
      //   styleOverrides: {
      //     loadingIndicator: ({ ownerState, theme }) => ({
      //       color: "rgba(0, 0, 0, 0.76)",
      //     }),
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderColor: theme.palette.text.base200,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.fontDarkest,
            marginBottom: "10px",
            height: "50px",
            // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
            //   borderColor: "unset",
            // },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: theme.palette.text.base200,
            },
            "&:not(.Mui-error):hover fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            //   borderWidth: "1px",
            //   borderColor: "unset",
            // },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginBottom: "10px",
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderRadius: "0px",
            borderWidth: "4px",
            backgroundColor: "#ececed",
            borderColor: "#ececed",
            paddingTop: "6px",
            paddingBottom: "6px",
            "&.Mui-selected": {
              color: "#383D47",
              //backgroundColor: '#000000'
            },
          }),
        },
      },
      // MuiPickersDay: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       borderRadius: "6px",
      //       "&.Mui-selected,&.Mui-selected:hover,&:focus.Mui-selected": {
      //         backgroundColor: "#383D47",
      //         borderRadius: "6px",
      //       },
      //       "&.MuiPickersDay-today": { borderRadius: "6px" },
      //       "&:hover": { backgroundColor: "#f7f7f7" },
      //     }),
      //   },
      // },
      // MuiPickersCalendarHeader: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //     }),
      //   },
      // },
      // MuiPickersYear: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       "& > .Mui-selected": { backgroundColor: "#383D47 !important" },
      //     }),
      //   },
      // },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginTop: "-5px",
            marginLeft: "0px",
            marginBottom: "10px",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&:focus-visible,fieldset:focus-visible": { outline: "none" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-icon": {
              marginInlineEnd: theme.spacing(3),
            },
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-action": {
              marginInlineStart: theme.spacing(2),
              paddingInlineStart: theme.spacing(2),
            },
          }),
        },
      },
    },
  },
  healthyKitchen: {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1440,
        xl: 1536,
      },
    },
    spacing: 4,
    // status: {
    //   danger: "#e53e3e",
    // },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
    },
    palette: {
      primary: {
        main: "#BAE2E2",
        dark: "#5DBBBB",
        light: "#D8EEEE",
        text: "#18191B",
        contrastText: "#18191B",
      },
      secondary: {
        main: "#E7C6A5",
        dark: "#DEB082",
        light: "#F2E0CF",
        text: "#18191B",
        contrastText: "#fff",
      },
      tertiary: {
        main: "#136FD7",
        dark: "#0b6ddb",
        light: "#BAE2E2",
        text: "#fff",
        contrastText: "#C2C4C7",
      },
      neutral: {
        text: "#8A8E99",
        dark: "#272C37",
        main: "#64748B",
        contrastText: "#fff",
      },
      error: {
        main: "#EA6060",
        dark: "#DA4242",
        light: "#EF8D8D",
        text: "#fff",
        contrastText: "#fff",
      },
      success: {
        main: "#70CC95",
        dark: "#56AC78",
        light: "#A4E3BD",
        text: "#fff",
        contrastText: "#fff",
      },
      warning: {
        main: "#FBBB55",
        dark: "#EAA331",
        light: "#FFCF83",
        text: "#fff",
        contrastText: "#fff",
      },
      info: {
        main: "#5894D5",
        dark: "#4584C9",
        light: "#8FB7E2",
        text: "#fff",
        contrastText: "#fff",
      },
      text: {
        primary: "#2C2E30",
        primaryLighter: "#F4FAFA",
        primaryLight: "#D8EEEE",
        primaryDefault: "#BAE2E2",
        primaryDark: "#5DBBBB",
        primaryDarkest: "#307373",

        secondaryLighter: "#FDFAF7",
        secondaryLight: "#F2E0CF",
        secondaryDefault: "#E7C6A5",
        secondaryDark: "#DEB082",
        secondaryDarkest: "#895725",

        successLighter: "#E1F9EA",
        successLight: "#A4E3BD",
        successDefault: "#70CC95",
        successDark: "#56AC78",
        successDarkest: "#2F533D",

        warningLighter: "#FFF4E3",
        warningLight: "#FFCF83",
        warningDefault: "#FBBB55",
        warningDark: "#EAA331",
        warningDarkest: "#6C4911",

        errorLighter: "#FBE1E1",
        errorLight: "#EF8D8D",
        errorDefault: "#EA6060",
        errorDark: "#DA4242",
        errorDarkest: "#591D1D",

        infoLighter: "#E9F1F9",
        infoLight: "#8FB7E2",
        infoDefault: "#5894D5",
        infoDark: "#4584C9",
        infoDarkest: "#18304B",

        base50: "#F3F4F6",
        base100: "#E5E8EB",
        base200: "#D7DAE0",
        base300: "#CACDD4",
        base400: "#C1C5CD",
        base500: "#B0B5BF",
        base600: "#9DA3AF",
        base700: "#848B9A",
        base800: "#676F7E",
        base850: "#3E4451",
        base900: "#323743",
        base950: "#292D38",

        fontLighter: "#F1F2F3",
        fontLight: "#A1A6AB",
        fontDefault: "#54595F",
        fontDark: "#2C2E30",
        fontDarkest: "#18191B",

        white: "#fff",
        fire: "#f9720f",
        bodyBg: "#fff",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@fontFace": ["Inter"],
          },
          body: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textTransform: "capitalize",
            borderRadius: "12px",
            padding: theme.spacing(2, 3),
            ...(ownerState.variant === "contained" && ownerState.color === "primary"
              ? {
                  "&": { color: theme.palette.primary.text, backgroundColor: theme.palette.primary.main },
                  "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                }
              : ownerState.variant === "contained" && ownerState.color === "secondary"
                ? {
                    "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                    "&.MuiLoadingButton-root.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }
                : ownerState.variant === "contained" && ownerState.color === "secondary"
                  ? {
                      "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                      "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      "&:hover": { color: theme.palette.text.primary },
                    }
                  : ownerState.variant === "contained" && ownerState.color === "warning"
                    ? {
                        "&": {
                          color: theme.palette.text.secondaryDark,
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      }
                    : ownerState.variant === "contained" && ownerState.color === "error"
                      ? {
                          "&": {
                            color: theme.palette.text.errorDark,
                            backgroundColor: theme.palette.text.errorLighter,
                          },
                          "&:hover": { backgroundColor: theme.palette.text.errorLighter },
                        }
                      : ownerState.variant === "outlined" && ownerState.color === "primary"
                        ? {
                            "&": { color: theme.palette.text.primaryDarkest, borderColor: theme.palette.primary.main },
                            "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.text,
                            },
                          }
                        : ownerState.variant === "outlined" && ownerState.color === "secondary"
                          ? {
                              "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                              "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.text,
                              },
                            }
                          : ownerState.variant === "contained" && ownerState.color === "secondary"
                            ? {
                                "&": { color: theme.palette.tertiary.main, borderColor: theme.palette.tertiary.main },
                                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              }
                            : {
                                backgroundColor: theme.palette.text.base100,
                                color: theme.palette.text.base900,
                                "&:hover": { backgroundColor: theme.palette.text.base100 },
                              }),
            ...(ownerState.size === "large"
              ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
              : ownerState.size === "medium"
                ? {
                    fontSize: "16px",
                    lineHeight: `${ownerState.variant === "outlined" ? "16px" : "18px"}`,
                    fontWeight: 500,
                    padding: theme.spacing(4, 6),
                  }
                : ownerState.size === "small"
                  ? { fontSize: "14px", lineHeight: "20px", fontWeight: 400, padding: theme.spacing(2, 3) }
                  : ownerState.size === "xsmall"
                    ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
                    : {}),
          }),
        },
      },
      // MuiLoadingButton: {
      //   styleOverrides: {
      //     loadingIndicator: ({ ownerState, theme }) => ({
      //       color: "rgba(0, 0, 0, 0.76)",
      //     }),
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderColor: theme.palette.text.base200,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.fontDarkest,
            marginBottom: "10px",
            height: "50px",
            // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
            //   borderColor: "unset",
            // },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: theme.palette.text.base200,
            },
            "&:not(.Mui-error):hover fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            //   borderWidth: "1px",
            //   borderColor: "unset",
            // },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginBottom: "10px",
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderRadius: "0px",
            borderWidth: "4px",
            backgroundColor: "#ececed",
            borderColor: "#ececed",
            paddingTop: "6px",
            paddingBottom: "6px",
            "&.Mui-selected": {
              color: "#383D47",
              //backgroundColor: '#000000'
            },
          }),
        },
      },
      // MuiPickersDay: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       borderRadius: "6px",
      //       "&.Mui-selected,&.Mui-selected:hover,&:focus.Mui-selected": {
      //         backgroundColor: "#383D47",
      //         borderRadius: "6px",
      //       },
      //       "&.MuiPickersDay-today": { borderRadius: "6px" },
      //       "&:hover": { backgroundColor: "#f7f7f7" },
      //     }),
      //   },
      // },
      // MuiPickersCalendarHeader: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //     }),
      //   },
      // },
      // MuiPickersYear: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       "& > .Mui-selected": { backgroundColor: "#383D47 !important" },
      //     }),
      //   },
      // },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginTop: "-5px",
            marginLeft: "0px",
            marginBottom: "10px",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&:focus-visible,fieldset:focus-visible": { outline: "none" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-icon": {
              marginInlineEnd: theme.spacing(3),
            },
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-action": {
              marginInlineStart: theme.spacing(2),
              paddingInlineStart: theme.spacing(2),
            },
          }),
        },
      },
    },
  },
  secondBite: {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1440,
        xl: 1536,
      },
    },
    spacing: 4,
    // status: {
    //   danger: "#e53e3e",
    // },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
    },
    palette: {
      primary: {
        main: "#CC3333",
        dark: "#A32929",
        light: "#D65C5C",
        text: "#fff",
        contrastText: "#fff",
      },
      secondary: {
        main: "#66CC66",
        dark: "#3DB83D",
        light: "#85D685",
        text: "#fff",
        contrastText: "#66CC66",
      },
      tertiary: {
        main: "#136FD7",
        dark: "#0b6ddb",
        light: "#BAE2E2",
        text: "#fff",
        contrastText: "#C2C4C7",
      },
      neutral: {
        text: "#8A8E99",
        dark: "#272C37",
        main: "#64748B",
        contrastText: "#fff",
      },
      error: {
        main: "#F45849",
        dark: "#DF3221",
        light: "#F67367",
        text: "#fff",
        contrastText: "#F45849",
      },
      success: {
        main: "#2EC885",
        dark: "#28A16D",
        light: "#53EDAA",
        text: "#fff",
        contrastText: "#2EC885",
      },
      warning: {
        main: "#FFA71A",
        dark: "#D28915",
        light: "#F5B54E",
        text: "#fff",
        contrastText: "#FFA71A",
      },
      info: {
        main: "#3B8FEB",
        dark: "#2F72BA",
        light: "#62AAF6",
        text: "#fff",
        contrastText: "#3B8FEB",
      },
      text: {
        primary: "#54545F",
        primaryLighter: "#FAEBEB",
        primaryLight: "#D65C5C",
        primaryDefault: "#CC3333",
        primaryDark: "#A32929",
        primaryDarkest: "#521414",

        secondaryLighter: "#F0FAF0",
        secondaryLight: "#85D685",
        secondaryDefault: "#66CC66",
        secondaryDark: "#3DB83D",
        secondaryDarkest: "#1F5C1F",

        successLighter: "#E6F9F1",
        successLight: "#53EDAA",
        successDefault: "#2EC885",
        successDark: "#28A16D",
        successDarkest: "#155338",

        warningLighter: "#FEF5E6",
        warningLight: "#F5B54E",
        warningDefault: "#FFA71A",
        warningDark: "#D28915",
        warningDarkest: "#66430A",

        errorLighter: "#FEEDEC",
        errorLight: "#F67367",
        errorDefault: "#F45849",
        errorDark: "#DF3221",
        errorDarkest: "#6F1911",

        infoLighter: "#E5F2FF",
        infoLight: "#62AAF6",
        infoDefault: "#3B8FEB",
        infoDark: "#2F72BA",
        infoDarkest: "#143C66",

        base50: "#F3F4F6",
        base100: "#E5E8EB",
        base200: "#D7DAE0",
        base300: "#CACDD4",
        base400: "#C1C5CD",
        base500: "#B0B5BF",
        base600: "#9DA3AF",
        base700: "#848B9A",
        base800: "#676F7E",
        base850: "#3E4451",
        base900: "#323743",
        base950: "#292D38",

        fontLighter: "#F1F2F3",
        fontLight: "#A1A1AB",
        fontDefault: "#54545F",
        fontDark: "#2C2C30",
        fontDarkest: "#18181B",

        white: "#fff",
        fire: "#f9720f",
        bodyBg: "#fff",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@fontFace": ["Inter"],
          },
          body: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textTransform: "capitalize",
            borderRadius: "12px",
            padding: theme.spacing(2, 3),
            ...(ownerState.variant === "contained" && ownerState.color === "primary"
              ? {
                  "&": { color: theme.palette.primary.text, backgroundColor: theme.palette.primary.main },
                  "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                }
              : ownerState.variant === "contained" && ownerState.color === "secondary"
                ? {
                    "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                    "&.MuiLoadingButton-root.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }
                : ownerState.variant === "contained" && ownerState.color === "secondary"
                  ? {
                      "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                      "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      "&:hover": { color: theme.palette.text.primary },
                    }
                  : ownerState.variant === "contained" && ownerState.color === "warning"
                    ? {
                        "&": {
                          color: theme.palette.text.secondaryDark,
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      }
                    : ownerState.variant === "contained" && ownerState.color === "error"
                      ? {
                          "&": {
                            color: theme.palette.text.errorDark,
                            backgroundColor: theme.palette.text.errorLighter,
                          },
                          "&:hover": { backgroundColor: theme.palette.text.errorLighter },
                        }
                      : ownerState.variant === "outlined" && ownerState.color === "primary"
                        ? {
                            "&": { color: theme.palette.text.primaryDefault, borderColor: theme.palette.primary.main },
                            "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.text,
                            },
                          }
                        : ownerState.variant === "outlined" && ownerState.color === "secondary"
                          ? {
                              "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                              "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.text,
                              },
                            }
                          : ownerState.variant === "contained" && ownerState.color === "secondary"
                            ? {
                                "&": { color: theme.palette.tertiary.main, borderColor: theme.palette.tertiary.main },
                                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              }
                            : {
                                backgroundColor: theme.palette.text.base100,
                                color: theme.palette.text.base900,
                                "&:hover": { backgroundColor: theme.palette.text.base100 },
                              }),
            ...(ownerState.size === "large"
              ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
              : ownerState.size === "medium"
                ? {
                    fontSize: "16px",
                    lineHeight: `${ownerState.variant === "outlined" ? "18px" : "26px"}`,
                    fontWeight: 500,
                    padding: theme.spacing(4, 6),
                  }
                : ownerState.size === "small"
                  ? { fontSize: "14px", lineHeight: "20px", fontWeight: 400, padding: theme.spacing(2, 3) }
                  : ownerState.size === "xsmall"
                    ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
                    : {}),
          }),
        },
      },
      // MuiLoadingButton: {
      //   styleOverrides: {
      //     loadingIndicator: ({ ownerState, theme }) => ({
      //       color: "rgba(0, 0, 0, 0.76)",
      //     }),
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderColor: theme.palette.text.base200,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.fontDarkest,
            marginBottom: "10px",
            height: "50px",
            // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
            //   borderColor: "unset",
            // },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: theme.palette.text.base200,
            },
            "&:not(.Mui-error):hover fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            //   borderWidth: "1px",
            //   borderColor: "unset",
            // },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginBottom: "10px",
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderRadius: "0px",
            borderWidth: "4px",
            backgroundColor: "#ececed",
            borderColor: "#ececed",
            paddingTop: "6px",
            paddingBottom: "6px",
            "&.Mui-selected": {
              color: "#383D47",
              //backgroundColor: '#000000'
            },
          }),
        },
      },
      // MuiPickersDay: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       borderRadius: "6px",
      //       "&.Mui-selected,&.Mui-selected:hover,&:focus.Mui-selected": {
      //         backgroundColor: "#383D47",
      //         borderRadius: "6px",
      //       },
      //       "&.MuiPickersDay-today": { borderRadius: "6px" },
      //       "&:hover": { backgroundColor: "#f7f7f7" },
      //     }),
      //   },
      // },
      // MuiPickersCalendarHeader: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //     }),
      //   },
      // },
      // MuiPickersYear: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       "& > .Mui-selected": { backgroundColor: "#383D47 !important" },
      //     }),
      //   },
      // },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginTop: "-5px",
            marginLeft: "0px",
            marginBottom: "10px",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&:focus-visible,fieldset:focus-visible": { outline: "none" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-icon": {
              marginInlineEnd: theme.spacing(3),
            },
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-action": {
              marginInlineStart: theme.spacing(2),
              paddingInlineStart: theme.spacing(2),
            },
          }),
        },
      },
    },
  },
  dietCenter: {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1440,
        xl: 1536,
      },
    },
    spacing: 4,
    // status: {
    //   danger: "#e53e3e",
    // },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
    },
    palette: {
      primary: {
        main: "#8BB61D",
        dark: "#6E9018",
        light: "#ACDD31",
        text: "#fff",
        contrastText: "#fff",
      },
      secondary: {
        main: "#EE4037",
        dark: "#D81C12",
        light: "#F1675F",
        text: "#fff",
        contrastText: "#EE4037",
      },
      tertiary: {
        main: "#136FD7",
        dark: "#0b6ddb",
        light: "#BAE2E2",
        text: "#fff",
        contrastText: "#C2C4C7",
      },
      neutral: {
        text: "#8A8E99",
        dark: "#272C37",
        main: "#64748B",
        contrastText: "#fff",
      },
      error: {
        main: "#F45849",
        dark: "#DF3221",
        light: "#F67367",
        text: "#fff",
        contrastText: "#F45849",
      },
      success: {
        main: "#2EC885",
        dark: "#28A16D",
        light: "#53EDAA",
        text: "#fff",
        contrastText: "#2EC885",
      },
      warning: {
        main: "#FFA71A",
        dark: "#D28915",
        light: "#F5B54E",
        text: "#fff",
        contrastText: "#FFA71A",
      },
      info: {
        main: "#3B8FEB",
        dark: "#2F72BA",
        light: "#62AAF6",
        text: "#fff",
        contrastText: "#3B8FEB",
      },
      text: {
        primary: "#54545F",

        primaryLighter: "#F4FBE5",
        primaryLight: "#ACDD31",
        primaryDefault: "#8BB61D",
        primaryDark: "#6E9018",
        primaryDarkest: "#394A0C",

        secondaryLighter: "#FDEDEC",
        secondaryLight: "#F1675F",
        secondaryDefault: "#EE4037",
        secondaryDark: "#D81C12",
        secondaryDarkest: "#6C0E09",

        successLighter: "#E6F9F1",
        successLight: "#53EDAA",
        successDefault: "#2EC885",
        successDark: "#28A16D",
        successDarkest: "#155338",

        warningLighter: "#FEF5E6",
        warningLight: "#F5B54E",
        warningDefault: "#FFA71A",
        warningDark: "#D28915",
        warningDarkest: "#66430A",

        errorLighter: "#FEEDEC",
        errorLight: "#F67367",
        errorDefault: "#F45849",
        errorDark: "#DF3221",
        errorDarkest: "#6F1911",

        infoLighter: "#E5F2FF",
        infoLight: "#62AAF6",
        infoDefault: "#3B8FEB",
        infoDark: "#2F72BA",
        infoDarkest: "#143C66",

        base50: "#F3F4F6",
        base100: "#E5E8EB",
        base200: "#D7DAE0",
        base300: "#CACDD4",
        base400: "#C1C5CD",
        base500: "#B0B5BF",
        base600: "#9DA3AF",
        base700: "#848B9A",
        base800: "#676F7E",
        base850: "#3E4451",
        base900: "#323743",
        base950: "#292D38",

        fontLighter: "#F1F2F3",
        fontLight: "#A1A1AB",
        fontDefault: "#54545F",
        fontDark: "#2C2C30",
        fontDarkest: "#18181B",

        white: "#fff",
        fire: "#f9720f",
        bodyBg: "#fff",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@fontFace": ["Inter"],
          },
          body: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textTransform: "capitalize",
            borderRadius: "12px",
            padding: theme.spacing(2, 3),
            ...(ownerState.variant === "contained" && ownerState.color === "primary"
              ? {
                  "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                  "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.text,
                    borderColor: theme.palette.secondary.main,
                  },
                }
              : ownerState.variant === "contained" && ownerState.color === "secondary"
                ? {
                    "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                    "&.MuiLoadingButton-root.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }
                : ownerState.variant === "contained" && ownerState.color === "secondary"
                  ? {
                      "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                      "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      "&:hover": { color: theme.palette.text.primary },
                    }
                  : ownerState.variant === "contained" && ownerState.color === "warning"
                    ? {
                        "&": {
                          color: theme.palette.text.secondaryDark,
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      }
                    : ownerState.variant === "contained" && ownerState.color === "error"
                      ? {
                          "&": {
                            color: theme.palette.text.errorDark,
                            backgroundColor: theme.palette.text.errorLighter,
                          },
                          "&:hover": { backgroundColor: theme.palette.text.errorLighter },
                        }
                      : ownerState.variant === "outlined" && ownerState.color === "primary"
                        ? {
                            "&": {
                              color: theme.palette.text.secondaryDefault,
                              borderColor: theme.palette.secondary.main,
                            },
                            "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                            "&:hover": {
                              backgroundColor: theme.palette.secondary.main,
                              color: theme.palette.secondary.text,
                              borderColor: theme.palette.secondary.main,
                            },
                          }
                        : ownerState.variant === "outlined" && ownerState.color === "secondary"
                          ? {
                              "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                              "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.text,
                              },
                            }
                          : ownerState.variant === "contained" && ownerState.color === "secondary"
                            ? {
                                "&": { color: theme.palette.tertiary.main, borderColor: theme.palette.tertiary.main },
                                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              }
                            : {
                                backgroundColor: theme.palette.text.base100,
                                color: theme.palette.text.base900,
                                "&:hover": { backgroundColor: theme.palette.text.base100 },
                              }),
            ...(ownerState.size === "large"
              ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
              : ownerState.size === "medium"
                ? {
                    fontSize: "16px",
                    lineHeight: `${ownerState.variant === "outlined" ? "18px" : "26px"}`,
                    fontWeight: 500,
                    padding: theme.spacing(4, 6),
                  }
                : ownerState.size === "small"
                  ? { fontSize: "14px", lineHeight: "20px", fontWeight: 400, padding: theme.spacing(2, 3) }
                  : ownerState.size === "xsmall"
                    ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
                    : {}),
          }),
        },
      },
      // MuiLoadingButton: {
      //   styleOverrides: {
      //     loadingIndicator: ({ ownerState, theme }) => ({
      //       color: "rgba(0, 0, 0, 0.76)",
      //     }),
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderColor: theme.palette.text.base200,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.fontDarkest,
            marginBottom: "10px",
            height: "50px",
            // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
            //   borderColor: "unset",
            // },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: theme.palette.text.base200,
            },
            "&:not(.Mui-error):hover fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            //   borderWidth: "1px",
            //   borderColor: "unset",
            // },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginBottom: "10px",
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderRadius: "0px",
            borderWidth: "4px",
            backgroundColor: "#ececed",
            borderColor: "#ececed",
            paddingTop: "6px",
            paddingBottom: "6px",
            "&.Mui-selected": {
              color: "#383D47",
            },
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginTop: "-5px",
            marginLeft: "0px",
            marginBottom: "10px",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&:focus-visible,fieldset:focus-visible": { outline: "none" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-icon": {
              marginInlineEnd: theme.spacing(3),
            },
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-action": {
              marginInlineStart: theme.spacing(2),
              paddingInlineStart: theme.spacing(2),
            },
          }),
        },
      },
    },
  },
  lowCal: {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1440,
        xl: 1536,
      },
    },
    spacing: 4,
    // status: {
    //   danger: "#e53e3e",
    // },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
    },
    palette: {
      primary: {
        main: "#FE7B03",
        dark: "#E56F01",
        light: "#FEA34D",
        text: "#fff",
        contrastText: "#fff",
      },
      secondary: {
        main: "#BFE035",
        dark: "#89A711",
        light: "#CEE869",
        text: "#0F0F0D",
        contrastText: "#fff",
      },
      tertiary: {
        main: "#136FD7",
        dark: "#0b6ddb",
        light: "#BAE2E2",
        text: "#fff",
        contrastText: "#C2C4C7",
      },
      neutral: {
        text: "#8A8E99",
        dark: "#272C37",
        main: "#64748B",
        contrastText: "#fff",
      },
      error: {
        main: "#DC3540",
        dark: "#BB202B",
        light: "#E35E67",
        text: "#fff",
        contrastText: "#DC3540",
      },
      success: {
        main: "#28A745",
        dark: "#1F8437",
        light: "#39D05D",
        text: "#fff",
        contrastText: "#28A745",
      },
      warning: {
        main: "#FFC107",
        dark: "#D19D00",
        light: "#F5B54E",
        text: "#fff",
        contrastText: "#FFC107",
      },
      info: {
        main: "#17A2B8",
        dark: "#128091",
        light: "#25CAE4",
        text: "#fff",
        contrastText: "#17A2B8",
      },
      text: {
        primary: "#56564D",
        primaryLighter: "#FFF2E6",
        primaryLight: "#FEA34D",
        primaryDefault: "#FE7B03",
        primaryDark: "#E56F01",
        primaryDarkest: "#A85101",

        secondaryLighter: "#F1F7D9",
        secondaryLight: "#CEE869",
        secondaryDefault: "#BFE035",
        secondaryDark: "#89A711",
        secondaryDarkest: "#7D9617",

        successLighter: "#EFFBF1",
        successLight: "#39D05D",
        successDefault: "#28A745",
        successDark: "#1F8437",
        successDarkest: "#10421B",

        warningLighter: "#FEF8E7",
        warningLight: "#F5B54E",
        warningDefault: "#FFC107",
        warningDark: "#D19D00",
        warningDarkest: "#6B5000",

        errorLighter: "#FBE9EA",
        errorLight: "#E35E67",
        errorDefault: "#DC3540",
        errorDark: "#BB202B",
        errorDarkest: "#5B1015",

        infoLighter: "#E9F8FC",
        infoLight: "#25CAE4",
        infoDefault: "#17A2B8",
        infoDark: "#128091",
        infoDarkest: "#094049",

        base50: "#EFEFE1",
        base100: "#E5E5DC",
        base200: "#D8D8CA",
        base300: "#CFCFBE",
        base400: "#C2C2AD",
        base500: "#B7B79E",
        base600: "#A8A88F",
        base700: "#8F8F76",
        base800: "#72725F",
        base850: "#48483D",
        base900: "#393932",
        base950: "#2E2E28",

        fontLighter: "#d3d3c3",
        fontLight: "#9F9F93",
        fontDefault: "#56564D",
        fontDark: "#333328",
        fontDarkest: "#1E1E15",

        white: "#fff",
        fire: "#f9720f",
        bodyBg: "#FFFFF5",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@fontFace": ["Inter"],
          },
          body: {
            backgroundColor: "#FFFFF5",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textTransform: "capitalize",
            borderRadius: "12px",
            padding: theme.spacing(2, 3),
            ...(ownerState.variant === "contained" && ownerState.color === "primary"
              ? {
                  "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                  "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.text,
                    borderColor: theme.palette.secondary.main,
                  },
                }
              : ownerState.variant === "contained" && ownerState.color === "secondary"
                ? {
                    "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                    "&.MuiLoadingButton-root.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }
                : ownerState.variant === "contained" && ownerState.color === "secondary"
                  ? {
                      "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                      "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      "&:hover": { color: theme.palette.text.primary },
                    }
                  : ownerState.variant === "contained" && ownerState.color === "warning"
                    ? {
                        "&": {
                          color: theme.palette.text.secondaryDark,
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      }
                    : ownerState.variant === "contained" && ownerState.color === "error"
                      ? {
                          "&": {
                            color: theme.palette.text.errorDark,
                            backgroundColor: theme.palette.text.errorLighter,
                          },
                          "&:hover": { backgroundColor: theme.palette.text.errorLighter },
                        }
                      : ownerState.variant === "outlined" && ownerState.color === "primary"
                        ? {
                            "&": { color: theme.palette.text.secondaryDark, borderColor: theme.palette.secondary.main },
                            "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                            "&:hover": {
                              backgroundColor: theme.palette.secondary.main,
                              color: theme.palette.secondary.text,
                              borderColor: theme.palette.secondary.main,
                            },
                          }
                        : ownerState.variant === "outlined" && ownerState.color === "secondary"
                          ? {
                              "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                              "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.text,
                                borderColor: theme.palette.secondary.main,
                              },
                            }
                          : ownerState.variant === "contained" && ownerState.color === "secondary"
                            ? {
                                "&": { color: theme.palette.tertiary.main, borderColor: theme.palette.tertiary.main },
                                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              }
                            : {
                                backgroundColor: theme.palette.text.base100,
                                color: theme.palette.text.base900,
                                "&:hover": { backgroundColor: theme.palette.text.base100 },
                              }),
            ...(ownerState.size === "large"
              ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
              : ownerState.size === "medium"
                ? {
                    fontSize: "16px",
                    lineHeight: `${ownerState.variant === "outlined" ? "18px" : "26px"}`,
                    fontWeight: 500,
                    padding: theme.spacing(4, 6),
                  }
                : ownerState.size === "small"
                  ? { fontSize: "14px", lineHeight: "20px", fontWeight: 400, padding: theme.spacing(2, 3) }
                  : ownerState.size === "xsmall"
                    ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
                    : {}),
          }),
        },
      },
      // MuiLoadingButton: {
      //   styleOverrides: {
      //     loadingIndicator: ({ ownerState, theme }) => ({
      //       color: "rgba(0, 0, 0, 0.76)",
      //     }),
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderColor: theme.palette.text.base200,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.fontDarkest,
            marginBottom: "10px",
            height: "50px",
            // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
            //   borderColor: "unset",
            // },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: theme.palette.text.base200,
            },
            "&:not(.Mui-error):hover fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            //   borderWidth: "1px",
            //   borderColor: "unset",
            // },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginBottom: "10px",
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderRadius: "0px",
            borderWidth: "4px",
            backgroundColor: "#ececed",
            borderColor: "#ececed",
            paddingTop: "6px",
            paddingBottom: "6px",
            "&.Mui-selected": {
              color: "#383D47",
              //backgroundColor: '#000000'
            },
          }),
        },
      },
      // MuiPickersDay: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       borderRadius: "6px",
      //       "&.Mui-selected,&.Mui-selected:hover,&:focus.Mui-selected": {
      //         backgroundColor: "#383D47",
      //         borderRadius: "6px",
      //       },
      //       "&.MuiPickersDay-today": { borderRadius: "6px" },
      //       "&:hover": { backgroundColor: "#f7f7f7" },
      //     }),
      //   },
      // },
      // MuiPickersCalendarHeader: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //     }),
      //   },
      // },
      // MuiPickersYear: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       "& > .Mui-selected": { backgroundColor: "#383D47 !important" },
      //     }),
      //   },
      // },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginTop: "-5px",
            marginLeft: "0px",
            marginBottom: "10px",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&:focus-visible,fieldset:focus-visible": { outline: "none" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-icon": {
              marginInlineEnd: theme.spacing(3),
            },
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-action": {
              marginInlineStart: theme.spacing(2),
              paddingInlineStart: theme.spacing(2),
            },
          }),
        },
      },
    },
  },
  highProtein: {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1440,
        xl: 1536,
      },
    },
    spacing: 4,
    // status: {
    //   danger: "#e53e3e",
    // },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
    },
    palette: {
      primary: {
        main: "#287279",
        dark: "#205B61",
        light: "#538E94",
        text: "#fff",
        contrastText: "#fff",
      },
      secondary: {
        main: "#FF9700",
        dark: "#D98000",
        light: "#FFB140",
        text: "#0D0E0F",
        contrastText: "#fff",
      },
      tertiary: {
        main: "#136FD7",
        dark: "#0b6ddb",
        light: "#BAE2E2",
        text: "#fff",
        contrastText: "#C2C4C7",
      },
      neutral: {
        text: "#8A8E99",
        dark: "#272C37",
        main: "#64748B",
        contrastText: "#fff",
      },
      error: {
        main: "#DC3540",
        dark: "#BB202B",
        light: "#E35E67",
        text: "#fff",
        contrastText: "#DC3540",
      },
      success: {
        main: "#28A745",
        dark: "#1F8437",
        light: "#39D05D",
        text: "#fff",
        contrastText: "#28A745",
      },
      warning: {
        main: "#FFC107",
        dark: "#D19D00",
        light: "#F5B54E",
        text: "#fff",
        contrastText: "#FFC107",
      },
      info: {
        main: "#17A2B8",
        dark: "#128091",
        light: "#25CAE4",
        text: "#fff",
        contrastText: "#17A2B8",
      },
      text: {
        primary: "#0D0E0F",
        primaryLighter: "#E9F1F2",
        primaryLight: "#538E94",
        primaryDefault: "#287279",
        primaryDark: "#205B61",
        primaryDarkest: "#14393D",

        secondaryLighter: "#FFF5E5",
        secondaryLight: "#FFB140",
        secondaryDefault: "#FF9700",
        secondaryDark: "#D98000",
        secondaryDarkest: "#804C00",

        successLighter: "#EFFBF1",
        successLight: "#39D05D",
        successDefault: "#28A745",
        successDark: "#1F8437",
        successDarkest: "#10421B",

        warningLighter: "#FEF8E7",
        warningLight: "#F5B54E",
        warningDefault: "#FFC107",
        warningDark: "#D19D00",
        warningDarkest: "#6B5000",

        errorLighter: "#FBE9EA",
        errorLight: "#E35E67",
        errorDefault: "#DC3540",
        errorDark: "#BB202B",
        errorDarkest: "#5B1015",

        infoLighter: "#E9F8FC",
        infoLight: "#25CAE4",
        infoDefault: "#17A2B8",
        infoDark: "#128091",
        infoDarkest: "#094049",

        base50: "#E7E8E9",
        base100: "#DFE0E2",
        base200: "#CFD1D3",
        base300: "#C5C7C9",
        base400: "#B4B8BB",
        base500: "#A7ABAF",
        base600: "#979CA1",
        base700: "#7C8288",
        base800: "#63696E",
        base850: "#3E4246",
        base900: "#323639",
        base950: "#292B2E",

        fontLighter: "#F3F3F1",
        fontLight: "#9F9F93",
        fontDefault: "#56564D",
        fontDark: "#333328",
        fontDarkest: "#1E1E15",

        white: "#fff",
        fire: "#f9720f",
        bodyBg: "#fff",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@fontFace": ["Inter"],
          },
          body: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textTransform: "capitalize",
            borderRadius: "12px",
            padding: theme.spacing(2, 3),
            ...(ownerState.variant === "contained" && ownerState.color === "primary"
              ? {
                  "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                  "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.text,
                    borderColor: theme.palette.secondary.main,
                  },
                }
              : ownerState.variant === "contained" && ownerState.color === "secondary"
                ? {
                    "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                    "&.MuiLoadingButton-root.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }
                : ownerState.variant === "contained" && ownerState.color === "secondary"
                  ? {
                      "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                      "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      "&:hover": { color: theme.palette.text.primary },
                    }
                  : ownerState.variant === "contained" && ownerState.color === "warning"
                    ? {
                        "&": {
                          color: theme.palette.text.secondaryDark,
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.text.secondaryLighter,
                        },
                        "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                      }
                    : ownerState.variant === "contained" && ownerState.color === "error"
                      ? {
                          "&": {
                            color: theme.palette.text.errorDark,
                            backgroundColor: theme.palette.text.errorLighter,
                          },
                          "&:hover": { backgroundColor: theme.palette.text.errorLighter },
                        }
                      : ownerState.variant === "outlined" && ownerState.color === "primary"
                        ? {
                            "&": { color: theme.palette.text.secondaryDark, borderColor: theme.palette.secondary.main },
                            "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                            "&:hover": {
                              backgroundColor: theme.palette.secondary.main,
                              color: theme.palette.secondary.text,
                            },
                          }
                        : ownerState.variant === "outlined" && ownerState.color === "secondary"
                          ? {
                              "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                              "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.text,
                              },
                            }
                          : ownerState.variant === "contained" && ownerState.color === "secondary"
                            ? {
                                "&": { color: theme.palette.tertiary.main, borderColor: theme.palette.tertiary.main },
                                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                              }
                            : {
                                backgroundColor: theme.palette.text.base100,
                                color: theme.palette.text.base900,
                                "&:hover": { backgroundColor: theme.palette.text.base100 },
                              }),
            ...(ownerState.size === "large"
              ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
              : ownerState.size === "medium"
                ? {
                    fontSize: "16px",
                    lineHeight: `${ownerState.variant === "outlined" ? "24px" : "26px"}`,
                    fontWeight: 500,
                    padding: theme.spacing(3, 4),
                  }
                : ownerState.size === "small"
                  ? {
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: 600,
                      padding: theme.spacing(2, 3),
                    }
                  : ownerState.size === "xsmall"
                    ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
                    : {}),
          }),
        },
      },
      // MuiLoadingButton: {
      //   styleOverrides: {
      //     loadingIndicator: ({ ownerState, theme }) => ({
      //       color: "rgba(0, 0, 0, 0.76)",
      //     }),
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderColor: theme.palette.text.base200,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.fontDarkest,
            marginBottom: "10px",
            height: "50px",
            // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
            //   borderColor: "unset",
            // },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: theme.palette.text.base200,
            },
            "&:not(.Mui-error):hover fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.base200,
              borderWidth: "1px",
            },
            // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            //   borderWidth: "1px",
            //   borderColor: "unset",
            // },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginBottom: "10px",
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            borderRadius: "0px",
            borderWidth: "4px",
            backgroundColor: "#ececed",
            borderColor: "#ececed",
            paddingTop: "6px",
            paddingBottom: "6px",
            "&.Mui-selected": {
              color: "#383D47",
              //backgroundColor: '#000000'
            },
          }),
        },
      },
      // MuiPickersDay: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       borderRadius: "6px",
      //       "&.Mui-selected,&.Mui-selected:hover,&:focus.Mui-selected": {
      //         backgroundColor: "#383D47",
      //         borderRadius: "6px",
      //       },
      //       "&.MuiPickersDay-today": { borderRadius: "6px" },
      //       "&:hover": { backgroundColor: "#f7f7f7" },
      //     }),
      //   },
      // },
      // MuiPickersCalendarHeader: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //     }),
      //   },
      // },
      // MuiPickersYear: {
      //   styleOverrides: {
      //     root: ({ ownerState, theme }) => ({
      //       color: "#383D47",
      //       "& > .Mui-selected": { backgroundColor: "#383D47 !important" },
      //     }),
      //   },
      // },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            marginTop: "-5px",
            marginLeft: "0px",
            marginBottom: "10px",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&:focus-visible,fieldset:focus-visible": { outline: "none" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-icon": {
              marginInlineEnd: theme.spacing(3),
            },
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-action": {
              marginInlineStart: theme.spacing(2),
              paddingInlineStart: theme.spacing(2),
            },
          }),
        },
      },
    },
  },
};
